import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Icon, Avatar, Spinner, CardTitle, StatusBadge } from '../../components/ui';

interface AdminUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  tier_id: string;
  tier_name: string;
  subscription_status: string | null;
  created_at: string;
}

function userInitials(user: AdminUser) {
  const first = (user.first_name || '').trim();
  const last = (user.last_name || '').trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || first.slice(0, 2).toUpperCase();
  }
  return user.email.slice(0, 2).toUpperCase();
}

export function AdminUsers() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<AdminUser | null>(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async (searchQuery?: string) => {
    setLoading(true);
    const result = await api.adminGetUsers({ limit: 50, search: searchQuery });
    if (result.success && result.data) {
      setUsers(result.data);
    } else {
      setError(result.error?.message || 'Failed to load users');
    }
    setLoading(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    loadUsers(search);
  };

  const handleEditRole = (user: AdminUser) => {
    setEditingUser(user);
    setEditRole(user.role);
    setMessage(null);
  };

  const handleSaveRole = async () => {
    if (!editingUser) return;

    setSaving(true);
    const result = await api.adminUpdateUser(editingUser.id, { role: editRole });
    if (result.success) {
      setMessage({ type: 'success', text: 'User role updated' });
      setEditingUser(null);
      await loadUsers(search);
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to update user' });
    }
    setSaving(false);
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters' });
      return;
    }

    setSaving(true);
    const result = await api.adminResetUserPassword(resetPasswordUser.id, newPassword);
    if (result.success) {
      setMessage({ type: 'success', text: `Password reset for ${resetPasswordUser.email}` });
      setResetPasswordUser(null);
      setNewPassword('');
    } else {
      setMessage({ type: 'error', text: result.error?.message || 'Failed to reset password' });
    }
    setSaving(false);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'admin':
        return <span className="badge badge-danger">Admin</span>;
      case 'user':
        return <span className="badge badge-gray">User</span>;
      default:
        return <span className="badge badge-gray">{role}</span>;
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'active':
        return <StatusBadge status="active" />;
      case 'past_due':
        return <StatusBadge status="past_due" />;
      case 'canceled':
        return <StatusBadge status="canceled" />;
      default:
        return <StatusBadge status="inactive" label="Free" />;
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card text-center py-12">
        <p className="text-[#F0928C] text-sm">{error}</p>
        <button onClick={() => loadUsers()} className="btn-ghost mt-4">
          Retry
        </button>
      </div>
    );
  }

  const rowGrid = 'md:grid md:grid-cols-[1.6fr_90px_90px_110px_90px_auto] md:gap-3 md:items-center';

  return (
    <div className="fx">
      {message && (
        <div
          className={`mb-5 px-4 py-3 rounded-xl border text-[13px] font-medium ${
            message.type === 'success'
              ? 'border-[#2E8B57]/40 bg-[#2E8B57]/10 text-[#7BD0A0]'
              : 'border-[#D8362F]/40 bg-[#D8362F]/10 text-[#F0928C]'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Search */}
      <form onSubmit={handleSearch} className="mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 max-w-md min-w-[220px]">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mist-500 pointer-events-none">
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              className="input pl-10"
            />
          </div>
          <button type="submit" className="btn-grad px-4 py-[11px]">
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                loadUsers();
              }}
              className="btn-ghost"
            >
              Clear
            </button>
          )}
        </div>
      </form>

      {/* Users list */}
      <div className="card">
        <div className="flex items-center justify-between gap-3">
          <CardTitle>Users</CardTitle>
          <span className="text-mist-500 text-xs font-mono">{users.length.toLocaleString()}</span>
        </div>

        {/* Column headers (desktop) */}
        <div className={`hidden ${rowGrid} mt-4 pb-2`}>
          {['User', 'Role', 'Tier', 'Status', 'Joined', ''].map((h, i) => (
            <span
              key={i}
              className={`text-mist-500 text-[11px] font-semibold uppercase tracking-[0.1em] ${
                i === 5 ? 'text-right' : ''
              }`}
            >
              {h}
            </span>
          ))}
        </div>

        <div className="mt-2 md:mt-0">
          {users.map((user) => (
            <div key={user.id} className={`${rowGrid} flex flex-col gap-2 py-3 border-t border-ink-800`}>
              <div className="flex items-center gap-[11px] min-w-0">
                <Avatar text={userInitials(user)} size={34} />
                <div className="min-w-0">
                  <p className="m-0 text-white text-[13.5px] font-semibold truncate">
                    {user.first_name || user.last_name
                      ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                      : 'No name'}
                  </p>
                  <p className="mt-px mb-0 text-mist-500 text-xs truncate">{user.email}</p>
                </div>
              </div>

              <div>
                {editingUser?.id === user.id ? (
                  <select
                    value={editRole}
                    onChange={(e) => setEditRole(e.target.value)}
                    className="input w-24 px-2 py-1.5 text-[12.5px]"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  getRoleBadge(user.role)
                )}
              </div>

              <span className="text-mist-400 text-[13px]">{user.tier_name || 'Free'}</span>

              <div>{getStatusBadge(user.subscription_status)}</div>

              <span className="text-mist-500 text-xs font-mono">{formatDate(user.created_at)}</span>

              <div className="flex md:justify-end gap-2 flex-wrap">
                {editingUser?.id === user.id ? (
                  <>
                    <button
                      onClick={handleSaveRole}
                      disabled={saving}
                      className="btn-grad px-3 py-[7px] text-[11px]"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => setEditingUser(null)}
                      className="btn-ghost px-3 py-[7px] text-xs"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEditRole(user)}
                      className="btn-ghost px-3 py-[7px] text-xs"
                    >
                      Edit role
                    </button>
                    <button
                      onClick={() => {
                        setResetPasswordUser(user);
                        setNewPassword('');
                        setMessage(null);
                      }}
                      className="btn-ghost px-3 py-[7px] text-xs"
                    >
                      Reset password
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && (
            <p className="text-center text-mist-500 text-sm py-10 m-0 border-t border-ink-800">
              No users found
            </p>
          )}
        </div>
      </div>

      {/* Reset Password Modal */}
      {resetPasswordUser && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50 p-4"
          style={{ background: 'rgba(0,0,0,.6)' }}
        >
          <div className="bg-ink-850 border border-ink-700 rounded-2xl p-6 w-full max-w-md shadow-[0_30px_80px_rgba(0,0,0,.6)]">
            <h3 className="font-display font-extrabold text-white text-[17px] m-0 mb-2">
              Reset password
            </h3>
            <p className="text-mist-400 text-[13px] mt-0 mb-4">
              Set a new password for{' '}
              <strong className="text-mist-200 font-semibold">{resetPasswordUser.email}</strong>
            </p>
            <div className="mb-5">
              <label className="label">New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 8 characters)"
                className="input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setResetPasswordUser(null);
                  setNewPassword('');
                }}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={saving || newPassword.length < 8}
                className="btn-grad"
              >
                {saving ? 'Resetting...' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
